type SchedulerWindow = Window & {
  scheduler?: { yield?: () => Promise<void> }
}

/**
 * Yields control back to the browser's main thread, so pending user input/paint work
 * can be processed before we continue running our own (synchronous) JS.
 * Uses the Scheduler API's `yield()` where available (Chromium), falling back to a
 * macrotask via `setTimeout` elsewhere (Safari/Firefox).
 */
export function yieldToMain (): Promise<void> {
  const scheduler = (window as SchedulerWindow).scheduler
  const schedulerYield = scheduler?.yield
  if (schedulerYield) {
    return schedulerYield.call(scheduler)
  }
  return new Promise(resolve => setTimeout(resolve, 0))
}

/**
 * Runs `callback` once the browser has idle time, but no later than `timeout` ms,
 * so it doesn't get delayed indefinitely on a never-idle page.
 * Falls back to `setTimeout` on browsers without `requestIdleCallback` (Safari).
 */
export function runWhenIdle (callback: () => void, timeout = 50): void {
  if (typeof window.requestIdleCallback === 'function') {
    window.requestIdleCallback(callback, { timeout })
  } else {
    setTimeout(callback, 0)
  }
}
